import MinusCircle from "@/assets/icons/minus-circle.svg";
import DeleteAlertDialog from "@/components/DeleteAlertDialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { useContextBarAppearance } from "@/contexts/ContextBarAppearanceContext";
import TodoItem from "@/domain/entities/TodoItem";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

type TodoLineProps = {
    todo: TodoItem;
    handleCompletionToggle: (todo: TodoItem) => void;
    handleRemoveTodo: (todo: TodoItem) => void;
};

const TodoAcceptedLine = ({
    todo,
    handleCompletionToggle,
    handleRemoveTodo,
}: TodoLineProps) => {
    const { t } = useTranslation();
    const { isDeepFocus } = useContextBarAppearance();

    return (
        <div
            className={cn(
                "flex items-center justify-between gap-2 rounded-sm px-1 hover:bg-surface-primary",
                isDeepFocus && "hover:bg-white/10",
            )}
        >
            <div className="flex items-center gap-2">
                <TooltipProvider delayDuration={100}>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Checkbox
                                checked={todo.done}
                                onCheckedChange={() => {
                                    handleCompletionToggle(todo);
                                }}
                            />
                        </TooltipTrigger>
                        <TooltipContent>
                            {todo.done
                                ? t("todo.uncompleteTask")
                                : t("todo.completeTask")}
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
                <Button
                    className={cn(isDeepFocus && "text-white")}
                    onClick={(e) => {
                        e.stopPropagation();
                        handleCompletionToggle(todo);
                    }}
                    size="blank"
                    variant="paragraph"
                >
                    <span className={`${todo.done ? "line-through" : ""}`}>
                        {todo.taskSummary}
                    </span>
                </Button>
            </div>
            <DeleteAlertDialog
                description={t("todo.deleteDialog.description")}
                handleDeleteAction={() => handleRemoveTodo(todo)}
                title={t("todo.deleteDialog.title")}
                trigger={
                    <TooltipProvider delayDuration={100}>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    className="ml-auto min-w-8 hover:bg-surface-textArea/25"
                                    size="smallCircle"
                                    variant="invisible"
                                >
                                    <img
                                        alt=""
                                        className="h-4 w-4"
                                        src={MinusCircle}
                                    />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                                {t("todo.removeTask")}
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                }
            />
        </div>
    );
};

export default TodoAcceptedLine;
